import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import type { Catalogs } from '@/@types/Catalogs.type';
import type { RootState } from '@/redux/store';
import useFeaturesReady from '@/shared/featureFlags/useFeaturesReady';
import { getInvoicingCatalog } from '@/shared/services/external.service';
import { INVOICE_AI_ASSISTANT_FLAG } from '../../constants/flag';
import {
  ASSISTANT_MAX_MESSAGES,
  ASSISTANT_MIN_MESSAGE_LENGTH,
  assistantErrorOf,
  reportAssistantOutcome,
  sendAssistantTurn,
  type AssistantMessage,
  type AssistantTurn,
} from '../../services/invoiceAssistant.service';
import { useInvoiceV2Tracking } from '../../hooks/useInvoiceV2Tracking';
import { INVOICE_V2_ROUTES } from '../constants/routes';
import { useInvoiceFlow } from '../hooks/useInvoiceFlow';
import { createEmptyDraft, type InvoiceDraft } from '../lib/draft';
import { applyAssistantDraft, buildClientDraft, stampBlockReason } from './assistantDraft';
import type { AssistantStep, ChatItem, Conversation, DraftBinding } from './types';

const TITLE_LENGTH = 40;

const STEP_BY_PATH: Record<string, AssistantStep> = {
  [INVOICE_V2_ROUTES.CONCEPTS]: 'conceptos',
  [INVOICE_V2_ROUTES.PAYMENT]: 'pago',
  [INVOICE_V2_ROUTES.REVIEW]: 'revisar',
};

const emptyConversation = (): Conversation => ({
  id: crypto.randomUUID(),
  title: '',
  items: [],
  messages: [],
  fieldStatus: {},
  questions: [],
});

const lastMessages = (messages: AssistantMessage[]) => messages.slice(-ASSISTANT_MAX_MESSAGES);

interface InvoiceAssistantValue {
  isOpen: boolean;
  toggle: () => void;
  close: () => void;
  step: AssistantStep;
  conversation: Conversation;
  history: Conversation[];
  isThinking: boolean;
  send: (text: string) => void;
  /** Solo el último cambio vigente se deshace: restaurar uno viejo tiraría los de después. */
  undoableChangeId: string | undefined;
  undo: (changeId: string) => void;
  newChat: () => void;
  openConversation: (id: string) => void;
  /** Por qué no se puede timbrar el borrador que llenó el asistente, o `null`. */
  stampBlock: (draft: InvoiceDraft) => string | null;
  /** Cierra la medición del back con el uuid, si la factura salió de una sesión. */
  reportStamped: (invoiceUuid: string) => void;
  /** La sesión del back que llenó ESTE borrador, si fue el asistente. Une el timbrado con la sesión. */
  sessionForDraft: (draftId: string) => string | undefined;
}

const InvoiceAssistantContext = createContext<InvoiceAssistantValue | null>(null);

/** Lo que cada tarjeta de cambio necesita para deshacerse, y para medir el deshacer. */
interface ChangeSnapshot {
  draft: InvoiceDraft;
  binding?: DraftBinding;
  appliedDraftId: string;
  sessionId: string;
  turnIndex: number;
  fieldsApplied: number;
}

/**
 * Estado del asistente, por encima del home y de los tres pasos para que la
 * plática sobreviva la navegación. El historial vive en memoria: es una beta.
 *
 * Habla con `POST /web/invoice-assistant/turn` de heru-fiscal-engine. El back
 * propone un borrador con el estado de cada campo; aquí se aplica lo que se
 * puede aplicar, con «Deshacer», y lo demás queda como pregunta o aviso. No
 * hay forma de timbrar desde el asistente.
 */
const InvoiceAssistantState = ({ enabled, children }: { enabled: boolean; children: ReactNode }) => {
  const { draft, replace, isTestEmission } = useInvoiceFlow();
  const tracking = useInvoiceV2Tracking();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const cachedCatalogs = useSelector((state: RootState) => state.profile.catalogs);

  const [isOpen, setIsOpen] = useState(false);
  const [conversation, setConversation] = useState<Conversation>(emptyConversation);
  const [history, setHistory] = useState<Conversation[]>([]);
  const [isThinking, setIsThinking] = useState(false);
  // El back apagado (503) o sin desplegar (404): se esconde todo el asistente.
  const [unavailable, setUnavailable] = useState(false);

  const draftRef = useRef(draft);
  const conversationRef = useRef(conversation);
  const snapshotsRef = useRef(new Map<string, ChangeSnapshot>());

  // El turno es asíncrono: al volver tiene que aplicarse sobre el borrador de
  // ESE momento, no el que existía al enviar.
  useEffect(() => {
    draftRef.current = draft;
    conversationRef.current = conversation;
  });

  const step = STEP_BY_PATH[location.pathname] ?? 'inicio';

  const unitNamesFor = useCallback(async () => {
    const catalogs =
      cachedCatalogs ??
      (await queryClient
        .fetchQuery<Catalogs>({
          queryKey: ['invoicing', 'sat-catalogs'],
          queryFn: getInvoicingCatalog,
          staleTime: Infinity,
        })
        .catch(() => undefined));
    const names = new Map(Object.values(catalogs?.key_unity ?? {}).map((unit) => [unit.code, unit.name]));
    return (code: string) => names.get(code) ?? code;
  }, [cachedCatalogs, queryClient]);

  /** Lo que un turno deja en el chat y en el borrador. */
  const absorb = useCallback(
    async (turn: AssistantTurn, sent: AssistantMessage[], bound: DraftBinding | undefined) => {
      const items: ChatItem[] = [{ kind: 'assistant', id: crypto.randomUUID(), text: turn.reply }];
      let binding = bound;

      // Solo `emitir` trae borrador: una duda o una deducción no deben terminar en un CFDI.
      if (turn.intent === 'emitir' && turn.draft) {
        const current = draftRef.current;
        const { next, lines } = applyAssistantDraft(current, turn.draft, await unitNamesFor());
        if (lines.length > 0) {
          const changeId = crypto.randomUUID();
          snapshotsRef.current.set(changeId, {
            draft: current,
            binding: bound,
            appliedDraftId: next.id,
            sessionId: turn.session_id,
            turnIndex: turn.turn_index,
            fieldsApplied: lines.length,
          });
          replace(next);
          items.push({ kind: 'change', id: changeId, lines, undone: false });
          tracking.trackAssistantDraftApplied({
            draftId: next.id,
            sessionId: turn.session_id,
            turnIndex: turn.turn_index,
            fieldsApplied: lines.length,
            undone: false,
          });
          if (next.receiver) tracking.trackFlowStarted(next, 'assistant');
          if (step === 'inicio' && next.receiver) navigate(INVOICE_V2_ROUTES.CONCEPTS);
        }
        binding = {
          draftId: next.id,
          baseline: lines.length > 0 ? next : (bound?.baseline ?? createEmptyDraft(current.issuer)),
        };
      }

      const escalations = turn.escalations.map((escalation) => escalation.question).filter(Boolean);
      if (escalations.length > 0) {
        items.push({
          kind: 'notice',
          id: crypto.randomUUID(),
          tone: 'taxes',
          text: `Esto lo revisa un contador de heru: ${escalations.join(' ')}`,
        });
      }
      if (turn.needs_human) {
        items.push({
          kind: 'notice',
          id: crypto.randomUUID(),
          tone: 'human',
          text:
            turn.needs_human.reason === 'fiscal'
              ? `Esto necesita a un contador. ${turn.needs_human.detail}`
              : `Esto lo tiene que ver alguien del equipo de heru. ${turn.needs_human.detail}`,
        });
      }

      setConversation((current) => ({
        ...current,
        items: [...current.items, ...items],
        messages: lastMessages([...sent, { role: 'assistant', text: turn.reply }]),
        sessionId: turn.session_id,
        fieldStatus: turn.draft?.field_status ?? current.fieldStatus,
        questions: turn.questions,
        binding,
      }));
    },
    [navigate, replace, step, tracking, unitNamesFor],
  );

  const send = useCallback(
    async (rawText: string) => {
      const text = rawText.trim();
      if (text.length < ASSISTANT_MIN_MESSAGE_LENGTH || isThinking) return;

      const before = conversationRef.current;
      const sent = lastMessages([...before.messages, { role: 'user', text }]);
      setConversation((current) => ({
        ...current,
        title: current.title || text.slice(0, TITLE_LENGTH),
        items: [...current.items, { kind: 'user', id: crypto.randomUUID(), text }],
        messages: sent,
        questions: [],
      }));
      setIsThinking(true);

      const current = draftRef.current;
      // Un borrador nuevo (se timbró o se descartó el anterior) ya no es el que llenó el asistente.
      const bound = before.binding?.draftId === current.id ? before.binding : undefined;
      const clientDraft = buildClientDraft(
        current,
        bound?.baseline ?? createEmptyDraft(current.issuer),
        bound ? before.fieldStatus : {},
      );
      const request = (sessionId?: string) =>
        sendAssistantTurn({
          ...(sessionId ? { session_id: sessionId } : {}),
          messages: sent,
          ...(clientDraft ? { client_draft: clientDraft } : {}),
        });

      try {
        let turn: AssistantTurn;
        try {
          turn = await request(before.sessionId);
        } catch (error) {
          // El historial viaja entero en cada turno: una sesión que el back ya no conoce se reabre sin id.
          if (!before.sessionId || assistantErrorOf(error).code !== 'invoice_assistant_session_unknown') {
            throw error;
          }
          turn = await request();
        }
        await absorb(turn, sent, bound);
      } catch (error) {
        const { status, code, message } = assistantErrorOf(error);
        if (code === 'invoice_assistant_disabled' || status === 404) {
          setUnavailable(true);
          return;
        }
        const errorText =
          status === 429 && message
            ? message
            : code === 'invoice_assistant_message_too_short'
              ? 'Cuéntame un poco más para poder ayudarte.'
              : 'No pude responder. Intenta de nuevo.';
        // El mensaje que falló sale del historial: reenviarlo no debe duplicarlo.
        setConversation((latest) => ({
          ...latest,
          messages: before.messages,
          items: [...latest.items, { kind: 'error', id: crypto.randomUUID(), text: errorText }],
        }));
      } finally {
        setIsThinking(false);
      }
    },
    [absorb, isThinking],
  );

  const undoableChangeId = useMemo(
    () =>
      [...conversation.items]
        .reverse()
        .find((item): item is Extract<ChatItem, { kind: 'change' }> => item.kind === 'change' && !item.undone)
        ?.id,
    [conversation.items],
  );

  const undo = useCallback(
    (changeId: string) => {
      const snapshot = snapshotsRef.current.get(changeId);
      if (!snapshot) return;
      replace(snapshot.draft);
      tracking.trackAssistantDraftApplied({
        draftId: snapshot.appliedDraftId,
        sessionId: snapshot.sessionId,
        turnIndex: snapshot.turnIndex,
        fieldsApplied: snapshot.fieldsApplied,
        undone: true,
      });
      setConversation((current) => ({
        ...current,
        binding: snapshot.binding,
        items: current.items.map((item) =>
          item.kind === 'change' && item.id === changeId ? { ...item, undone: true } : item,
        ),
        // El back no guarda estado: sin esto volvería a proponer lo mismo en el siguiente turno.
        messages: lastMessages([
          ...current.messages,
          { role: 'user', text: 'Deshice los cambios que aplicaste al borrador.' },
        ]),
      }));
    },
    [replace, tracking],
  );

  const archive = useCallback((current: Conversation) => {
    if (current.items.length > 0) setHistory((past) => [current, ...past]);
  }, []);

  const newChat = useCallback(() => {
    if (isThinking) return;
    archive(conversation);
    setConversation(emptyConversation());
  }, [archive, conversation, isThinking]);

  const openConversation = useCallback(
    (id: string) => {
      const selected = history.find((past) => past.id === id);
      if (!selected || isThinking) return;
      setHistory((past) => [
        ...(conversation.items.length > 0 ? [conversation] : []),
        ...past.filter((candidate) => candidate.id !== id),
      ]);
      setConversation(selected);
    },
    [conversation, history, isThinking],
  );

  const stampBlock = useCallback(
    (current: InvoiceDraft) => {
      const { binding, fieldStatus, questions } = conversation;
      if (!binding || binding.draftId !== current.id) return null;
      return stampBlockReason(fieldStatus, questions, current, binding.baseline, isTestEmission);
    },
    [conversation, isTestEmission],
  );

  const toggle = useCallback(() => {
    if (!isOpen) {
      tracking.trackAssistantOpened({
        conversationId: conversation.id,
        draftId: draft.id,
        step,
        newConversation: conversation.items.length === 0,
      });
    }
    setIsOpen(!isOpen);
  }, [conversation.id, conversation.items.length, draft.id, isOpen, step, tracking]);

  const sessionForDraft = useCallback(
    (draftId: string) =>
      conversation.binding?.draftId === draftId ? conversation.sessionId : undefined,
    [conversation.binding?.draftId, conversation.sessionId],
  );

  const reportStamped = useCallback((invoiceUuid: string) => {
    const { sessionId, binding } = conversationRef.current;
    if (!sessionId || binding?.draftId !== draftRef.current.id) return;
    // Solo medición: si falla, la factura ya está timbrada y no hay nada que avisarle al usuario.
    reportAssistantOutcome(sessionId, invoiceUuid).catch(() => undefined);
  }, []);

  const value = useMemo<InvoiceAssistantValue>(
    () => ({
      isOpen,
      toggle,
      close: () => setIsOpen(false),
      step,
      conversation,
      history,
      isThinking,
      send: (text) => void send(text),
      undoableChangeId,
      undo,
      newChat,
      openConversation,
      stampBlock,
      reportStamped,
      sessionForDraft,
    }),
    [
      isOpen,
      toggle,
      step,
      conversation,
      history,
      isThinking,
      send,
      undoableChangeId,
      undo,
      newChat,
      openConversation,
      stampBlock,
      reportStamped,
      sessionForDraft,
    ],
  );

  return (
    <InvoiceAssistantContext.Provider value={enabled && !unavailable ? value : null}>
      {children}
    </InvoiceAssistantContext.Provider>
  );
};

/**
 * Detrás de la bandera `invoice-ai-assistant`. Mientras las features no estén
 * listas, o con la bandera apagada, el contexto es `null` y nada del asistente
 * se pinta. El árbol es el mismo en los dos casos a propósito: si la bandera
 * llega tarde, cambiar de envoltura remontaría la pantalla y perdería lo que
 * el usuario estaba capturando.
 */
export const InvoiceAssistantProvider = ({ children }: { children: ReactNode }) => {
  const featuresReady = useFeaturesReady();
  const isOn = useFeatureIsOn(INVOICE_AI_ASSISTANT_FLAG);
  return <InvoiceAssistantState enabled={featuresReady && isOn}>{children}</InvoiceAssistantState>;
};

/** `null` fuera del provider, con la bandera apagada o con el back apagado: quien lo use no pinta nada. */
export const useInvoiceAssistant = (): InvoiceAssistantValue | null =>
  useContext(InvoiceAssistantContext);
